import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MoveDown } from "lucide-react";
import { MaskedLines } from "./Reveal";

const HERO_IMG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_7d05bada-2779-41ea-90b1-d0980c2c7baf/artifacts/6k99sbcl_20260524_090420.webp";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex items-end overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 -top-[10%] h-[120%] w-full">
        <img
          src={HERO_IMG}
          alt="Interior renovation in progress"
          className="w-full h-full object-cover grayscale-[35%]"
        />
      </motion.div>
      {/* Overlays */}
      <div className="absolute inset-0 bg-brand-navy/80" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/40 to-brand-navy/70" />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-28"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="mb-6"
        >
          <span className="inline-flex items-center gap-3 text-xs md:text-sm uppercase tracking-[0.3em] text-brand-orange font-bold">
            <span className="h-px w-10 bg-brand-orange/60" /> Serving Florida & Virginia
          </span>
        </motion.div>

        <h1 className="font-heading text-5xl sm:text-6xl lg:text-8xl font-extrabold tracking-tighter leading-[0.95] text-brand-text max-w-4xl">
          <MaskedLines lines={["Quality Renovations.", "Built to Last."]} delay={0.35} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 max-w-xl text-lg md:text-xl text-brand-muted leading-relaxed"
          data-testid="hero-subtitle"
        >
          Craftsmanship you can trust — specializing in commercial build-outs and
          full residential remodels across Florida and Virginia.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href="#contact"
            className="group inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-orangeHover text-white font-semibold px-8 py-4 rounded-sm transition-colors"
            data-testid="hero-cta-quote"
          >
            Request a Free Quote
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
          </a>
          <a
            href="#work"
            className="inline-flex items-center gap-3 border border-brand-hi hover:border-brand-text text-brand-text font-semibold px-8 py-4 rounded-sm transition-colors"
            data-testid="hero-cta-work"
          >
            View Our Work
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 hidden sm:flex items-center gap-2 text-brand-muted text-xs uppercase tracking-widest"
      >
        <MoveDown className="w-4 h-4 animate-bounce" strokeWidth={1.5} /> Scroll
      </motion.div>
    </section>
  );
}
